import React, { useEffect, useState } from 'react';
import { Col, Row, Space, notification } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { ThemeFilterBtn, ThemeModal, ThemeInput } from '../Styles/Theme';
import { useNavigate, useParams } from 'react-router-dom';
import { ThemeNoSidebar } from './SurveysStyle';
import QuestionPreview from './Create/QuestionPreview';
import EditableTitle from '../../Components/common/EditableTitle';
import { connect } from 'react-redux';
import {
  addSurveyQuestionApi,
  addSurveyQuestionReset,
  deleteSurveyQuestionApi,
  deleteSurveyQuestionReset,
  getSurveyDetailApi,
  getSurveyDetailReset,
  sendSurveyPromptApi,
  sendSurveyPromptReset,
  updateSurveyQuestionApi,
  updateSurveyQuestionReset,
  updateSurveyDemographicsApi,
  updateSurveyDemographicsReset,
  saveSurveyApi,
  saveSurveyReset,
  regenerateQuestionsApi,
  regenerateQuestionsReset,
  reorderQuestionsApi,
  reorderQuestionsReset,
  copySurveyApi,
  copySurveyReset,
} from "../../store/actions/surveyAction";
import axios from "axios";
import config from "../../config";
import { getUserToken } from "../../utils/helpers";
import { startSSE } from "../../utils/api/sseClient";
import { useSurveyPermissions } from "../../utils/permissions";

const CreateSurvey = ({
  getSurveyDetailApi,
  getSurveyDetailReset,
  sendSurveyPromptApi,
  sendSurveyPromptReset,
  addSurveyQuestionApi,
  addSurveyQuestionReset,
  updateSurveyQuestionApi,
  updateSurveyQuestionReset,
  deleteSurveyQuestionApi,
  deleteSurveyQuestionReset,
  updateSurveyDemographicsApi,
  updateSurveyDemographicsReset,
  saveSurveyApi,
  saveSurveyReset,
  regenerateQuestionsApi,
  regenerateQuestionsReset,
  reorderQuestionsApi,
  reorderQuestionsReset,
  copySurveyApi,
  copySurveyReset,
  getSurveyDetailState,
  sendSurveyPromptState,
  addSurveyQuestionState,
  updateSurveyQuestionState,
  deleteSurveyQuestionState,
  updateSurveyDemographicsState,
  saveSurveyState,
  regenerateQuestionsState,
  reorderQuestionsState,
  copySurveyState,
}) => {
  const navigate = useNavigate();
  const { surveyId } = useParams();
  
  // Permission checks
  const { can } = useSurveyPermissions();

  const [isMobile, setIsMobile] = useState(false);
  const [survey, setSurvey] = useState(null);
  const [messages, setMessages] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regeneratingQuestionId, setRegeneratingQuestionId] = useState(null);
  const [shouldNavigateToAIPoll, setShouldNavigateToAIPoll] = useState(false);
  const [isTitleOnlySave, setIsTitleOnlySave] = useState(false);
  const [copyModalVisible, setCopyModalVisible] = useState(false);
  const [copyName, setCopyName] = useState('');

  useEffect(() => {
    if (!surveyId) {
      navigate("/surveys/");
      return;
    }
    getSurveyDetailApi(surveyId);
    return () => {
      getSurveyDetailReset();
    };
  }, [surveyId, getSurveyDetailApi, getSurveyDetailReset, navigate]);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);
    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  useEffect(() => {
    if (getSurveyDetailState.apiState === "success") {
      const data = getSurveyDetailState.data?.data || {};
      const surveyData = data.survey || null;
      setSurvey(surveyData);
      setMessages(data.messages || []);
      setQuestions(data.questions || []);
      getSurveyDetailReset();
    } else if (getSurveyDetailState.apiState === "error") {
      notification.error({
        message: getSurveyDetailState.message || "Unable to load survey",
      });
      getSurveyDetailReset();
    }
  }, [getSurveyDetailState, getSurveyDetailReset]);

  useEffect(() => {
    if (sendSurveyPromptState.apiState === "success") {
      const payload = sendSurveyPromptState.data?.data || {};
      const { userMessage, assistantMessage } = payload;
      setMessages((prev) => {
        const next = [...prev];
        if (userMessage) next.push(userMessage);
        if (assistantMessage) next.push(assistantMessage);
        return next;
      });
      sendSurveyPromptReset();
    } else if (sendSurveyPromptState.apiState === "error") {
      notification.error({
        message: sendSurveyPromptState.message || "Unable to send prompt",
      });
      sendSurveyPromptReset();
    }
  }, [sendSurveyPromptState, sendSurveyPromptReset]);

  const handleQuestionState = (slice, resetAction, successMessage) => {
    if (slice.apiState === "success") {
      const updated = slice.data?.data?.questions || [];
      setQuestions(updated);
      if (successMessage) {
        notification.success({ message: successMessage });
      }
      resetAction();
    } else if (slice.apiState === "error") {
      notification.error({
        message: slice.message || "Unable to update questions",
      });
      resetAction();
    }
  };

  useEffect(() => {
    handleQuestionState(addSurveyQuestionState, addSurveyQuestionReset, "Question added");
  }, [addSurveyQuestionState, addSurveyQuestionReset]);

  useEffect(() => {
    handleQuestionState(updateSurveyQuestionState, updateSurveyQuestionReset, "Question updated");
  }, [updateSurveyQuestionState, updateSurveyQuestionReset]);

  useEffect(() => {
    handleQuestionState(deleteSurveyQuestionState, deleteSurveyQuestionReset, "Question deleted");
  }, [deleteSurveyQuestionState, deleteSurveyQuestionReset]);

  useEffect(() => {
    if (!surveyId) return;
    const token = getUserToken();
    if (!token) return;
    const stop = startSSE({
      url: `${config.api.base_url}/surveys/stream/${surveyId}`,
      headers: { Authorization: `Bearer ${token}` },
      onEvent: (event, payload) => {
        if (!payload) return;
        if (event === "token") {
          setMessages((prev) =>
            prev.map((msg) =>
              msg._id === payload.messageId
                ? {
                    ...msg,
                    message_text: (msg.message_text || "") + (payload.token || ""),
                    stream_state: "streaming",
                  }
                : msg
            )
          );
        } else if (event === "clarification_needed") {
          // NOTE: This event should no longer be sent (demographics are optional now)
          // Handle gracefully - just update the message, don't block UI
          setMessages((prev) =>
            prev.map((msg) =>
              msg._id === payload.messageId
                ? {
                    ...msg,
                    message_text: payload.messageText || msg.message_text,
                    stream_state: "complete",
                  }
                : msg
            )
          );
          // Only update topic from AI response, not other demographics
          if (payload.demographics?.topic) {
            setSurvey((prev) => ({
              ...(prev || {}),
              demographics: {
                ...(prev?.demographics || {}),
                topic: payload.demographics.topic,
              },
            }));
          }
          if (payload.surveyTitle) {
            setSurvey((prev) => ({ ...prev, title: payload.surveyTitle }));
          }
        } else if (event === "info_only") {
          setMessages((prev) =>
            prev.map((msg) =>
              msg._id === payload.messageId
                ? {
                    ...msg,
                    message_text: payload.messageText || msg.message_text,
                    stream_state: "complete",
                  }
                : msg
            )
          );
          // Only update topic from AI response, not other demographics
          if (payload.demographics?.topic) {
            setSurvey((prev) => ({
              ...(prev || {}),
              demographics: {
                ...(prev?.demographics || {}),
                topic: payload.demographics.topic,
              },
            }));
          }
          if (payload.surveyTitle) {
            setSurvey((prev) => ({
              ...prev,
              title: payload.surveyTitle,
            }));
          }
        } else if (event === "complete") {
          setMessages((prev) =>
            prev.map((msg) =>
              msg._id === payload.messageId
                ? {
                    ...msg,
                    message_text: payload.messageText || msg.message_text,
                    stream_state: "complete",
                  }
                : msg
            )
          );
          if (payload.questions) {
            setQuestions(payload.questions);
          }
          // Update survey status to ready - only update topic from AI, not other demographics
          setSurvey((prev) => ({
            ...(prev || {}),
            status: 'ready',
            ...(payload.demographics?.topic && {
              demographics: {
                ...(prev?.demographics || {}),
                topic: payload.demographics.topic,
              },
            }),
            ...(payload.surveyTitle && { title: payload.surveyTitle }),
          }));
          setIsRegenerating(false);
          setRegeneratingQuestionId(null);
        } else if (event === "error") {
          notification.error({
            message: payload.error || "Unable to generate questions",
          });
          setIsRegenerating(false);
          setRegeneratingQuestionId(null);
        } else if (event === "regeneration_started") {
          if (payload.regenerateQuestionId) {
            setRegeneratingQuestionId(payload.regenerateQuestionId);
          } else {
            setIsRegenerating(true);
          }
        } else if (event === "regeneration_complete") {
          // Show success notification
          setRegeneratingQuestionId((currentId) => {
            if (currentId && payload.questions) {
              // Single question regeneration
              const sortedQuestions = [...payload.questions].sort((a, b) => a.order_index - b.order_index);
              const questionIndex = sortedQuestions.findIndex(q => q._id === currentId);
              if (questionIndex !== -1) {
                notification.success({
                  key: `regenerate-success-${currentId}`,
                  message: `Q${questionIndex + 1} regenerated successfully`,
                });
              }
            } else {
              // Full regeneration (Save & Regenerate)
              notification.success({
                key: 'regenerate-all-success',
                message: 'Questions regenerated successfully',
              });
            }
            return null;
          });
          if (payload.questions) {
            setQuestions(payload.questions);
          }
          // Update survey status to ready - only update topic from AI, not other demographics
          setSurvey((prev) => ({
            ...(prev || {}),
            status: payload.status || 'ready',
            ...(payload.demographics?.topic && {
              demographics: {
                ...(prev?.demographics || {}),
                topic: payload.demographics.topic,
              },
            }),
          }));
          setIsRegenerating(false);
        } else if (event === "regeneration_error") {
          notification.error({
            message: payload.error || "Unable to regenerate questions",
          });
          setIsRegenerating(false);
          setRegeneratingQuestionId(null);
        }
      },
      onError: () => {
        notification.error({
          message: "Connection lost. Please refresh the page.",
        });
      },
    });
    return () => {
      stop && stop();
    };
  }, [surveyId]);

  const handleSendPrompt = (text) => {
    if (!text || !text.trim()) {
      notification.error({ message: "Please enter a message" });
      return;
    }
    sendSurveyPromptApi({
      surveyId,
      messageText: text.trim(),
    });
  };

  const handleAddQuestion = (questionText, media) => {
    if (!questionText || !questionText.trim()) {
      notification.error({ message: "Question text is required" });
      return;
    }
    const payload = { surveyId, questionText: questionText.trim() };
    if (media) payload.media = media;
    addSurveyQuestionApi(payload);
  };

  const handleUpdateQuestion = (questionId, questionText, followups, media) => {
    const payload = {
      surveyId,
      questionId,
      questionText: questionText.trim(),
    };
    if (followups !== undefined) {
      payload.followups = followups;
    }
    if (media !== undefined) {
      payload.media = media;
    }
    updateSurveyQuestionApi(payload);
  };

  const handleDeleteQuestion = (questionId) => {
    deleteSurveyQuestionApi({
      surveyId,
      questionId,
    });
  };

  const handleSaveDemographics = (values) => {
    updateSurveyDemographicsApi({
      surveyId,
      demographics: values,
    });
  };

  const handleRegenerateAllQuestions = () => {
    setIsRegenerating(true);
    regenerateQuestionsApi({ surveyId, demographics: survey?.demographics });
  };

  const handleSaveAndRegenerateQuestions = (demographicsToSave) => {
    updateSurveyDemographicsApi({
      surveyId,
      demographics: demographicsToSave,
    });
    setIsRegenerating(true);
    regenerateQuestionsApi({ surveyId, demographics: demographicsToSave });
  };

  const handleRegenerateQuestion = (questionId) => {
    setRegeneratingQuestionId(questionId);
    regenerateQuestionsApi({ surveyId, regenerateQuestionId: questionId, demographics: survey?.demographics });
  };

  const handleSurveyTitleChange = (newTitle) => {
    setSurvey((prev) => ({
      ...prev,
      title: newTitle,
    }));
  };

  const handleSaveSurveyTitle = async (newTitle) => {
    setIsTitleOnlySave(true);
    setSurvey((prev) => ({ ...prev, title: newTitle }));
    await saveSurveyApi({ surveyId, title: newTitle });
  };

  const handleSaveSurvey = () => {
    setShouldNavigateToAIPoll(false);
    saveSurveyApi({
      surveyId,
      title: survey?.title,
    });
  };

  const handleSaveAndBuildAIPoll = () => {
    setShouldNavigateToAIPoll(true);
    saveSurveyApi({
      surveyId,
      title: survey?.title,
    });
  };

  const handleReorderQuestions = (orderedIds) => {
    reorderQuestionsApi({
      surveyId,
      questionIds: orderedIds,
    });
  };

  const handleInsertQuestion = (afterIndex, questionText, media) => {
    if (!questionText || !questionText.trim()) return;
    const payload = { surveyId, questionText: questionText.trim(), afterIndex };
    if (media) payload.media = media;
    addSurveyQuestionApi(payload);
  };

  const handleImportQuestions = async (parsedQuestions) => {
    try {
      const res = await axios.post(
        `${config.api.base_url}/surveys/questions/import`,
        { surveyId, questions: parsedQuestions },
        { headers: { Authorization: 'Bearer ' + getUserToken() } }
      );
      if (res.data?.data?.questions) {
        setQuestions(res.data.data.questions);
        notification.success({
          message: 'Questions Imported',
          description: `${parsedQuestions.length} question${parsedQuestions.length !== 1 ? 's' : ''} imported successfully.`,
          duration: 3,
        });
      }
    } catch (err) {
      notification.error({ message: 'Failed to import questions' });
      throw err;
    }
  };

  useEffect(() => {
    if (updateSurveyDemographicsState.apiState === "success") {
      const updatedSurvey = updateSurveyDemographicsState.data?.data?.survey;
      if (updatedSurvey) {
        setSurvey(updatedSurvey);
      }
      updateSurveyDemographicsReset();
    } else if (updateSurveyDemographicsState.apiState === "error") {
      notification.error({
        message: updateSurveyDemographicsState.message || "Unable to save target audience",
      });
      updateSurveyDemographicsReset();
    }
  }, [updateSurveyDemographicsState, updateSurveyDemographicsReset]);

  useEffect(() => {
    if (regenerateQuestionsState.apiState === "error") {
      notification.error({
        message: regenerateQuestionsState.message || "Unable to start regeneration",
      });
      setIsRegenerating(false);
      setRegeneratingQuestionId(null);
      regenerateQuestionsReset();
    } else if (regenerateQuestionsState.apiState === "success") {
      regenerateQuestionsReset();
    }
  }, [regenerateQuestionsState, regenerateQuestionsReset]);

  // Handle reorder questions state
  useEffect(() => {
    if (reorderQuestionsState.apiState === "success") {
      const updated = reorderQuestionsState.data?.data?.questions || [];
      if (updated.length > 0) {
        setQuestions(updated);
      }
      reorderQuestionsReset();
    } else if (reorderQuestionsState.apiState === "error") {
      notification.error({
        message: reorderQuestionsState.message || "Unable to reorder questions",
      });
      // Force reset localQuestions in QuestionPreview by creating new array reference
      // This triggers the useEffect that syncs localQuestions from sortedQuestions
      setQuestions((prev) => [...prev]);
      reorderQuestionsReset();
    }
  }, [reorderQuestionsState, reorderQuestionsReset]);

  useEffect(() => {
    if (saveSurveyState.apiState === "success") {
      if (!isTitleOnlySave) {
        notification.success({
          message: "Survey saved successfully",
        });
      }
      if (shouldNavigateToAIPoll) {
        navigate("/polling-agents/polling-agent/create-polling-agent?survey=" + surveyId);
      }
      setIsTitleOnlySave(false);
      saveSurveyReset();
    } else if (saveSurveyState.apiState === "error") {
      notification.error({
        message: saveSurveyState.message || "Unable to save survey",
      });
      setIsTitleOnlySave(false);
      saveSurveyReset();
    }
  }, [saveSurveyState, saveSurveyReset, shouldNavigateToAIPoll, navigate, surveyId, isTitleOnlySave]);

  // Handle copy survey state
  useEffect(() => {
    if (copySurveyState.apiState === "success") {
      const newSurveyId = copySurveyState.data?.data?.survey_id;
      notification.success({ message: "Survey copied successfully" });
      setCopyModalVisible(false);
      setCopyName('');
      copySurveyReset();
      if (newSurveyId) {
        navigate(`/surveys/study/${newSurveyId}`, { replace: true });
      }
    } else if (copySurveyState.apiState === "error") {
      notification.error({
        message: copySurveyState.message || "Unable to copy survey",
      });
      copySurveyReset();
    }
  }, [copySurveyState, copySurveyReset, navigate]);

  const surveyTitle = survey?.title || "Survey";
  const isLoadingDetail = getSurveyDetailState.apiState === "loading";

  return (
    <>
      <ThemeNoSidebar>
        <Row gutter={[16, 16]} className="mb-4">
          <Col xs={24}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Space
                type="horizontal"
                style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  lineHeight: '16px',
                  color: '#3D2969',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                <span>
                  <ThemeFilterBtn
                    size="medium"
                    classNames="btnGrayBorder"
                    icon={<img src="/images/back-icon.svg" alt="Back" />}
                    className="noBackground mr-2"
                    onClick={() => navigate(-1)}
                  />
                </span>
                <EditableTitle
                  value={survey?.title}
                  onSave={handleSaveSurveyTitle}
                  placeholder="Untitled Survey"
                />
              </Space>
              {questions.length > 1 && can('duplicate') && (
                <ThemeFilterBtn
                  icon={<CopyOutlined />}
                  onClick={() => {
                    setCopyName('');
                    setCopyModalVisible(true);
                  }}
                  loading={copySurveyState.apiState === 'loading'}
                >
                  Copy & Create New
                </ThemeFilterBtn>
              )}
            </div>
          </Col>
        </Row>
        <QuestionPreview
          surveyTitle={surveyTitle}
          messages={messages}
          questions={questions}
          demographics={survey?.demographics}
          onSendMessage={handleSendPrompt}
          isSending={sendSurveyPromptState.apiState === "loading"}
          onAddQuestion={handleAddQuestion}
          onUpdateQuestion={handleUpdateQuestion}
          onDeleteQuestion={handleDeleteQuestion}
          onSaveDemographics={handleSaveDemographics}
          isSavingDemographics={updateSurveyDemographicsState.apiState === "loading"}
          onSaveSurvey={can('save') ? handleSaveSurvey : null}
          onSaveAndBuildAIPoll={can('save') ? handleSaveAndBuildAIPoll : null}
          isSavingSurvey={saveSurveyState.apiState === "loading"}
          onRegenerateAllQuestions={can('regenerate') ? handleRegenerateAllQuestions : null}
          onSaveAndRegenerateQuestions={can('regenerate') ? handleSaveAndRegenerateQuestions : null}
          onRegenerateQuestion={can('regenerate') ? handleRegenerateQuestion : null}
          isRegenerating={isRegenerating}
          regeneratingQuestionId={regeneratingQuestionId}
          onReorderQuestions={handleReorderQuestions}
          onInsertQuestion={handleInsertQuestion}
          onSurveyTitleChange={handleSurveyTitleChange}
          isLoading={isLoadingDetail}
          canExport={can('export')}
          canImport={can('import')}
          onImportQuestions={handleImportQuestions}
        />
      </ThemeNoSidebar>

      {/* Copy & Create New Modal */}
      <ThemeModal
        title="Copy & Create New"
        open={copyModalVisible}
        onCancel={() => {
          setCopyModalVisible(false);
          setCopyName('');
        }}
        onOk={() => copySurveyApi(surveyId, copyName.trim())}
        okText="Create"
        okButtonProps={{
          disabled: !copyName.trim(),
          loading: copySurveyState.apiState === 'loading',
        }}
        width={480}
        destroyOnClose
      >
        <div style={{ marginBottom: 8 }}>
          <label>Survey Name</label>
        </div>
        <ThemeInput
          size="large"
          placeholder="Enter survey name"
          value={copyName}
          onChange={(e) => setCopyName(e.target.value)}
          maxLength={100}
          onPressEnter={() => {
            if (copyName.trim()) copySurveyApi(surveyId, copyName.trim());
          }}
        />
      </ThemeModal>
    </>
  );
};

const mapStateToProps = (state) => ({
  getSurveyDetailState: state.survey.getSurveyDetailState,
  sendSurveyPromptState: state.survey.sendSurveyPromptState,
  addSurveyQuestionState: state.survey.addSurveyQuestionState,
  updateSurveyQuestionState: state.survey.updateSurveyQuestionState,
  deleteSurveyQuestionState: state.survey.deleteSurveyQuestionState,
  updateSurveyDemographicsState: state.survey.updateSurveyDemographicsState,
  saveSurveyState: state.survey.saveSurveyState,
  regenerateQuestionsState: state.survey.regenerateQuestionsState,
  reorderQuestionsState: state.survey.reorderQuestionsState,
  copySurveyState: state.survey.copySurveyState,
});

const mapDispatchToProps = {
  getSurveyDetailApi,
  getSurveyDetailReset,
  sendSurveyPromptApi,
  sendSurveyPromptReset,
  addSurveyQuestionApi,
  addSurveyQuestionReset,
  updateSurveyQuestionApi,
  updateSurveyQuestionReset,
  deleteSurveyQuestionApi,
  deleteSurveyQuestionReset,
  updateSurveyDemographicsApi,
  updateSurveyDemographicsReset,
  saveSurveyApi,
  saveSurveyReset,
  regenerateQuestionsApi,
  regenerateQuestionsReset,
  reorderQuestionsApi,
  reorderQuestionsReset,
  copySurveyApi,
  copySurveyReset,
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateSurvey);
